/**
 * Renewable Energy Utility Functions
 * 
 * This utility provides calculations and formatting functions specific to
 * renewable energy data visualization and analysis.
 */

import { smartFormatNumber } from './numberFormatting';
import { interpolateViridis } from 'd3-scale-chromatic';

export const clamp01 = (t) => Math.min(1, Math.max(0, t));

export const getViridisColor = (value, vmin, vmax) => {
  if (!Number.isFinite(value) || !Number.isFinite(vmin) || !Number.isFinite(vmax) || vmin === vmax) {
    return '#440154';
  }
  const t = clamp01((value - vmin) / (vmax - vmin));
  return interpolateViridis(t);
};

export const getViridisGradientStops = (stops = 12) => {
  const n = Math.max(2, Math.floor(stops));
  const parts = [];
  for (let i = 0; i < n; i++) {
    const t = i / (n - 1);
    parts.push(`${interpolateViridis(t)} ${(t * 100).toFixed(1)}%`);
  }
  return parts.join(', ');
};

/**
 * Calculate percentile thresholds for renewable energy capacity factors
 * @param {Array} capacityFactors - Array of capacity factor values
 * @param {string} type - Type of renewable energy ('solar', 'onshore_wind', 'offshore_wind')
 * @returns {Object} Threshold object with percentile values
 */
export const calculatePercentileThresholds = (capacityFactors, type = 'solar') => {
  if (!capacityFactors || capacityFactors.length === 0) {
    return {
      poor: { threshold: 0, color: '#440154' },
      fair: { threshold: 0, color: '#31688e' },
      good: { threshold: 0, color: '#35b779' },
      high: { threshold: 0, color: '#fde725' },
      excellent: { threshold: 0, color: '#fde725' }
    };
  }

  // Sort capacity factors in descending order (original logic)
  const sortedCFs = [...capacityFactors].sort((a, b) => b - a);
  const total = sortedCFs.length;
  
  // Calculate percentile thresholds (top 20%, 60th, 40th, 20th percentiles) - original logic
  const p80 = sortedCFs[Math.floor(total * 0.2)]; // Top 20%
  const p60 = sortedCFs[Math.floor(total * 0.4)]; // 60th percentile
  const p40 = sortedCFs[Math.floor(total * 0.6)]; // 40th percentile
  const p20 = sortedCFs[Math.floor(total * 0.8)]; // 20th percentile

  const thresholds = {
    excellent: { threshold: p80, color: '#FDE725' }, // Top 20%
    high: { threshold: p60, color: '#6DCD59' },     // 60th percentile
    good: { threshold: p40, color: '#35B779' },     // 40th percentile
    fair: { threshold: p20, color: '#31688E' },      // 20th percentile
    poor: { threshold: 0, color: '#440154' }         // Bottom
  };

  return thresholds;
};

/**
 * Format number with appropriate units
 * @param {number} num - Number to format
 * @returns {string} Formatted number string
 */
export const formatNumber = (num) => {
  // Handle null, undefined, or non-numeric values
  if (num == null || isNaN(num)) {
    return 'N/A';
  }
  
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  } else if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return num.toFixed(1);
};

/**
 * Get capacity factor label based on thresholds
 * @param {number} cf - Capacity factor value
 * @param {Object} thresholds - Threshold object
 * @returns {string} Label ('Poor', 'Fair', 'Good', 'High', 'Excellent')
 */
export const getCapacityFactorLabel = (cf, thresholds) => {
  if (!thresholds) return 'Unknown';
  
  if (cf >= thresholds.excellent.threshold) return 'Excellent';
  if (cf >= thresholds.high.threshold) return 'High';
  if (cf >= thresholds.good.threshold) return 'Good';
  if (cf >= thresholds.fair.threshold) return 'Fair';
  return 'Poor';
};

// ---------------------------------------------------------------------------
// Radial Fill helpers
// ---------------------------------------------------------------------------

const RADIAL_FILL_K_MIN = 0.05;
const RADIAL_FILL_AREA_CAP_KM2 = 2500;

/** Suitable area (km²) from API grid_data — canonical field: suitable_area_km2 */
export const getZoneSuitableAreaKm2 = (zone) => {
  const n = Number(zone?.suitable_area_km2);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

/**
 * Scale a GeoJSON polygon/multipolygon geometry toward/away from a centroid.
 * k=1 → original size; k=0.15 → 15% of original size centered on centroid.
 */
const scaleGeometryFromCentroid = (geometry, centroidLng, centroidLat, k) => {
  if (!geometry || k >= 1) return geometry;
  const scaleRing = (ring) =>
    ring.map(([lng, lat]) => [
      centroidLng + k * (lng - centroidLng),
      centroidLat + k * (lat - centroidLat),
    ]);
  if (geometry.type === 'Polygon') {
    return { type: 'Polygon', coordinates: geometry.coordinates.map(scaleRing) };
  }
  if (geometry.type === 'MultiPolygon') {
    return {
      type: 'MultiPolygon',
      coordinates: geometry.coordinates.map((poly) => poly.map(scaleRing)),
    };
  }
  return geometry;
};

/**
 * Build two GeoJSON feature collections for the radial-fill visualization:
 *   outlines → original cell boundary (used for the ghost grid)
 *   fills    → geometry scaled toward centroid proportional to suitable_area_km2
 *
 * Normalization: max(suitable_area_km2) over the entire gridData array
 * (one country + technology per call, so this is already the correct scope).
 *
 * k = kMin + (1 - kMin) * sqrt(area / maxArea)   — sqrt gives perceptual linearity
 */
export const buildRadialFillGeoJSON = (gridData, kMin = RADIAL_FILL_K_MIN) => {
  const empty = { type: 'FeatureCollection', features: [] };
  if (!gridData || gridData.length === 0) return { outlines: empty, fills: empty };

  const areaCap = RADIAL_FILL_AREA_CAP_KM2;

  const outlineFeatures = [];
  const fillFeatures = [];

  for (const zone of gridData) {
    if (!zone.geometry) continue;

    const centroidLng = Number(zone.lng);
    const centroidLat = Number(zone.lat);
    const area = Math.min(RADIAL_FILL_AREA_CAP_KM2, getZoneSuitableAreaKm2(zone));
    const k = kMin + (1 - kMin) * Math.sqrt(area / areaCap);
    const scaledGeometry = scaleGeometryFromCentroid(
      zone.geometry,
      centroidLng,
      centroidLat,
      k
    );

    outlineFeatures.push({ type: 'Feature', properties: zone, geometry: zone.geometry });
    fillFeatures.push({ type: 'Feature', properties: zone, geometry: scaledGeometry });
  }

  return {
    outlines: { type: 'FeatureCollection', features: outlineFeatures },
    fills: { type: 'FeatureCollection', features: fillFeatures },
  };
};

/**
 * Style for the inner (scaled) fill polygon in radial-fill mode.
 * No border — the ghost outline layer provides the cell boundary.
 */
export const getRadialFillStyle = (feature, thresholds) => {
  const zone = feature.properties;
  const cf =
    typeof zone?.avg_re_cf === 'number' && Number.isFinite(zone.avg_re_cf)
      ? zone.avg_re_cf
      : zone['Capacity Factor'];
  return {
    fillColor: getViridisColor(cf, thresholds.vmin, thresholds.vmax),
    color: 'transparent',
    weight: 0,
    opacity: 1,
    fillOpacity: 0.85,
  };
};

/** Ghost style for the full cell boundary outline (non-interactive layer). */
export const RADIAL_OUTLINE_STYLE = {
  fillColor: 'transparent',
  fillOpacity: 0,
  color: '#aaaaaa',
  weight: 0.4,
  opacity: 0.35,
};

// ---------------------------------------------------------------------------

/**
 * Optimize GeoJSON data based on current map view for performance
 * @param {Array} gridData - Array of grid data objects
 * @param {Object} mapBounds - Leaflet map bounds
 * @param {number} zoomLevel - Current zoom level
 * @returns {Object} Optimized GeoJSON data
 */
export const optimizeGeoJSONData = (gridData, mapBounds, zoomLevel) => {
  if (!gridData || gridData.length === 0) {
    return { type: 'FeatureCollection', features: [] };
  }


  // Performance optimization - temporarily disabled to show all data
  // const maxFeatures = zoomLevel <= 4 ? 5000 : zoomLevel <= 6 ? 10000 : zoomLevel <= 8 ? 20000 : 50000;
  
  let polygonCount = 0;
  let pointCount = 0;
  let errorCount = 0;
  
  const features = gridData.map(zone => {
    // Use geometry if available (already parsed GeoJSON object)
    if (zone.geometry) {
      try {
        polygonCount++;
        return {
          type: 'Feature',
          properties: zone,
          geometry: zone.geometry
        };
      } catch (error) {
        console.warn('Failed to use geometry:', error);
        errorCount++;
        return null;
      }
    }
    
    // Fallback to point geometry if no geometry
    if (zone.lat && zone.lng) {
      pointCount++;
      return {
        type: 'Feature',
        properties: zone,
        geometry: {
          type: 'Point',
          coordinates: [zone.lng, zone.lat]
        }
      };
    }
    
    return null;
  }).filter(feature => feature !== null);

  

  return {
    type: 'FeatureCollection',
    features
  };
};

/**
 * Create zone popup content
 * @param {Object} zone - Zone data object
 * @param {string} zoneType - Type of zone ('solar', 'onshore_wind', 'offshore_wind')
 * @returns {string} HTML popup content
 */
export const createZonePopup = (zone, zoneType) => {
  const typeLabels = {
    solar: 'Solar',
    onshore_wind: 'Onshore Wind',
    offshore_wind: 'Offshore Wind'
  };

  const rawCf = zone?.['Capacity Factor'];
  const clusterCf = zone?.avg_re_cf;
  const cfLine = (typeof clusterCf === 'number' && Number.isFinite(clusterCf))
    ? `<div><strong>Cluster CF:</strong> ${smartFormatNumber(clusterCf * 100)}%</div>
       <div><strong>Raw CF:</strong> ${smartFormatNumber((rawCf ?? 0) * 100)}%</div>`
    : `<div><strong>Capacity Factor:</strong> ${smartFormatNumber((rawCf ?? 0) * 100)}%</div>`;

  return `
    <div class="p-2">
      <h4 class="font-semibold text-sm mb-2">${typeLabels[zoneType]} Zone</h4>
      <div class="space-y-1 text-xs">
        ${cfLine}
        <div><strong>Capacity:</strong> ${smartFormatNumber(zone['Installed Capacity Potential (MW)'])} MW</div>
        <div><strong>Area:</strong> ${smartFormatNumber(getZoneSuitableAreaKm2(zone))} km²</div>
      </div>
    </div>
  `;
};

/**
 * Get hover style for map features
 * @param {number} zoomLevel - Current zoom level
 * @returns {Object} Leaflet style object
 */
export const getHoverStyle = (zoomLevel) => ({
  weight: zoomLevel <= 4 ? 1 : zoomLevel <= 6 ? 1.5 : zoomLevel <= 8 ? 2 : 2.5,
  opacity: 1,
  fillOpacity: 0.8,
  color: 'white',
  dashArray: '5'
});

/**
 * Get zone style based on capacity factor and thresholds
 * @param {Object} feature - GeoJSON feature
 * @param {string} zoneType - Type of zone
 * @param {Object} thresholds - Threshold object
 * @param {number} zoomLevel - Current zoom level
 * @returns {Object} Leaflet style object
 */
export const getZoneStyle = (feature, zoneType, thresholds, zoomLevel) => {
  const zone = feature.properties;
  const cf = (typeof zone?.avg_re_cf === 'number' && Number.isFinite(zone.avg_re_cf))
    ? zone.avg_re_cf
    : zone['Capacity Factor'];
  
  // Continuous viridis mode (PNG-matching for clustering overlays)
  if (thresholds?.mode === 'continuous') {
    const fillColor = getViridisColor(cf, thresholds.vmin, thresholds.vmax);
    return {
      fillColor,
      color: 'white',
      weight: 0.3,
      opacity: 1,
      fillOpacity: 0.8
    };
  }

  let color = '#440154'; // Default poor color (threshold/bucket mode)
  
  if (thresholds) {
    if (cf >= thresholds.excellent.threshold) color = thresholds.excellent.color;
    else if (cf >= thresholds.high.threshold) color = thresholds.high.color;
    else if (cf >= thresholds.good.threshold) color = thresholds.good.color;
    else if (cf >= thresholds.fair.threshold) color = thresholds.fair.color;
    else color = thresholds.poor.color;
  }

  // Return polygon style for GeoJSON layers
  return {
    fillColor: color,
    color: 'white',
    weight: zoomLevel <= 4 ? 0.5 : zoomLevel <= 6 ? 0.8 : zoomLevel <= 8 ? 1 : 1.2,
    opacity: 0.8,
    fillOpacity: 0.6
  };
};

const renewableUtils = {
  calculatePercentileThresholds,
  formatNumber,
  getCapacityFactorLabel,
  optimizeGeoJSONData,
  getZoneSuitableAreaKm2,
  buildRadialFillGeoJSON,
  getRadialFillStyle,
  RADIAL_OUTLINE_STYLE,
  createZonePopup,
  getHoverStyle,
  getZoneStyle
};

export default renewableUtils;
